import axios from "axios";
import {
  API_BASE_PATHS,
  API_ROUTES,
  IFreeTrialAccessTokenRequest,
  IMySubscriptionResponse,
  IMemberPlan,
  IMemberPlanRequest,
  ITrialTokenDetails,
  ITrialUserDetails,
  ISubscriptionUpgradePreview,
  IUpgradeSubscriptionResponse,
  IPaymentMethodUpdateResult,
  ICancelSubscriptionResponse,
} from "../libs";
import { buildRequestConfig } from "./buildRequestConfig";

class PaymentService {
  async getMySubscription(): Promise<IMySubscriptionResponse> {
    const config = await buildRequestConfig();
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.SUBSCRIPTION}`,
      config
    );
    return response.data.data;
  }

  async previewMySubscriptionUpgrade(packageId: string): Promise<ISubscriptionUpgradePreview> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.PREVIEW_SUBSCRIPTION_UPGRADE}`,
      { packageId },
      config
    );
    return response.data.data;
  }

  async upgradeMySubscription(
    packageId: string,
    prorationDate: number
  ): Promise<IUpgradeSubscriptionResponse> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.UPGRADE_SUBSCRIPTION}`,
      { packageId, prorationDate },
      config
    );
    return response.data.data;
  }

  async createPaymentMethodSetup(): Promise<{ clientSecret: string }> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.CREATE_PAYMENT_METHOD_SETUP}`,
      null,
      config
    );
    return response.data.data;
  }

  async completePaymentMethodUpdate(setupIntentId: string): Promise<IPaymentMethodUpdateResult> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.COMPLETE_PAYMENT_METHOD_UPDATE}`,
      { setupIntentId },
      config
    );
    return response.data.data;
  }

  async cancelMySubscription(): Promise<ICancelSubscriptionResponse> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.CANCEL_SUBSCRIPTION}`,
      null,
      config
    );
    return response.data.data;
  }

  async reactivateMySubscription(): Promise<ICancelSubscriptionResponse> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.REACTIVATE_SUBSCRIPTION}`,
      null,
      config
    );
    return response.data.data;
  }

  async getStripeCustomerAccountDetails(): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.GET_ACCOUNT_DETAILS}`,
      { ...config }
    );
    return response.data;
  }

  async createStripeCustomerAccount(): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.CREATE_ACCOUNT}`,
      { ...config }
    );
    return response.data;
  }

  async updateStripeCustomerAccount(accountId: string, bankToken: string): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.UPDATE_ACCOUNT}`,
      { accountId, bankToken },
      { ...config }
    );
    return response.data;
  }

  async saveStripeCustomerAccountDetails(accountId: string): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.SAVE_ACCOUNT}/${accountId}`,
      null,
      { ...config }
    );
    return response.data;
  }

  async createCustomerProductPlan(planData: IMemberPlanRequest): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.MEMBER_PLANS}`,
      planData,
      { ...config }
    );
    return response.data;
  }

  async updateCustomerProductPlan(planId: string, planData: IMemberPlanRequest): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.put(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.MEMBER_PLANS}/${planId}`,
      planData,
      { ...config }
    );
    return response.data;
  }

  async deleteCustomerProductPlan(planId: string): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.delete(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.MEMBER_PLANS}/${planId}`,
      { ...config }
    );
    return response.data;
  }

  async getCustomerProductPlans(): Promise<IMemberPlan[]> {
    const config = await buildRequestConfig();

    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.MEMBER_PLANS}`,
      { ...config }
    );

    return response.data.data;
  }

  async getUpdateAccountLink(accountId: string): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.GET_UPDATE_ACCOUNT_LINK}/${accountId}`,
      { ...config }
    );
    return response.data;
  }

  async createFreeTrialAccessToken(data: IFreeTrialAccessTokenRequest): Promise<any> {
    const config = await buildRequestConfig();

    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.FREE_TRIAL_ACCESS_TOKEN}`,
      data,
      { ...config }
    );
    return response.data;
  }

  async getFreeTrialAccessTokens(): Promise<ITrialTokenDetails[]> {
    const config = await buildRequestConfig();

    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.GET_FREE_TRIAL_ACCESS_TOKENS}`,
      { ...config }
    );
    return response.data.data.trialCodelist;
  }

  async getFreeTrialUsers(): Promise<ITrialUserDetails> {
    const config = await buildRequestConfig();

    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.GET_FREE_TRIAL_USERS}`,
      { ...config }
    );
    return response.data.data;
  }

  async getReferralPrograms(): Promise<any[]> {
    const config = await buildRequestConfig();
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.REFERRAL_PROGRAMS}`,
      config
    );
    return response.data.data;
  }

  async createReferralProgram(data: any): Promise<any> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.REFERRAL_PROGRAMS}`,
      data,
      config
    );
    return response.data.data;
  }

  async updateReferralProgram(id: string, data: any): Promise<any> {
    const config = await buildRequestConfig();
    const response = await axios.put(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.REFERRAL_PROGRAMS}/${id}`,
      data,
      config
    );
    return response.data.data;
  }

  async getReferralParticipants(id: string): Promise<any[]> {
    const config = await buildRequestConfig();
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.REFERRAL_PROGRAMS}/${id}/participants`,
      config
    );
    return response.data.data;
  }

  async inviteReferralParticipant(
    id: string,
    data: { email: string; invitationUrl?: string }
  ): Promise<any> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.REFERRAL_PROGRAMS}/${id}/participants`,
      data,
      config
    );
    return response.data.data;
  }

  async getReferralParticipantActivity(id: string): Promise<any> {
    const config = await buildRequestConfig();
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.CLIENT_API}/referral-admin/participants/${id}/activity`,
      config
    );
    return response.data.data;
  }

  async addReferralParticipantNote(id: string, text: string): Promise<any> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/referral-participants/${id}/notes`,
      { text },
      config
    );
    return response.data.data;
  }

  async deleteReferralParticipantNote(id: string, noteId: string): Promise<void> {
    const config = await buildRequestConfig();
    await axios.delete(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/referral-participants/${id}/notes/${noteId}`,
      config
    );
  }

  async getCoupons(): Promise<any[]> {
    const config = await buildRequestConfig();
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.COUPONS}`,
      config
    );
    return response.data.data;
  }

  async createCoupon(data: any): Promise<any> {
    const config = await buildRequestConfig();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.COUPONS}`,
      data,
      config
    );
    return response.data.data;
  }

  async updateCoupon(id: string, data: any): Promise<any> {
    const config = await buildRequestConfig();
    const response = await axios.put(
      `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.PAYMENTS_API}/${API_ROUTES.COUPONS}/${id}`,
      data,
      config
    );
    return response.data.data;
  }
}

export const paymentService = new PaymentService();
